/**
 * Wspólna logika wyznaczania danych masowych (Sekcja 13 - Mass in running
 * order) na podstawie kodu wersji pojazdu ({{version}}), niezależna od
 * konkretnej zabudowy. Same tabele danych (per zabudowa) mieszkają w osobnych
 * plikach - patrz IVECO_MASS_DATA (iveco-mass-data.ts) i odpowiedniki dla
 * pozostałych linii - i są tu tylko agregowane pod kluczem VehicleLine.
 *
 * @module vehicle-mass-data
 */

import { VehicleLine } from "../vehicle-line";
import { IVECO_MASS_DATA, expandIvecoVersionCandidates } from "./iveco-mass-data";
import { MERCEDES_TOURIST_LINE_HIGHDECK_MASS_DATA } from "./mercedes-tourist-line-highdeck-mass-data";
import { MERCEDES_TOURIST_LINE_MASS_DATA } from "./mercedes-tourist-line-mass-data";

/** Pojedynczy wpis danych masowych dla konkretnej wersji (+ liczby stojących pasażerów) */
export interface MassDataEntry {
  axle1RangeMin: number;
  axle1RangeMax: number;
  axle2Min: number;
  axle2Max: number;
  massInRunningOrderMin: number;
  massInRunningOrderMax: number;
}

/**
 * Klucz drugiego poziomu: liczba stojących pasażerów jako string ("0"/"1"),
 * albo "*" gdy w źródle liczba stojących pasażerów nie wpływa na wynik (tabela
 * dawała identyczne wartości "II oś max" i "Masa max" dla obu wariantów, albo
 * źródło w ogóle nie ma takiej kolumny - np. Mercedes Tourist Line HighDeck).
 */
export type StandingPassengersKey = "*" | "0" | "1";

/** Tabela danych masowych dla jednej zabudowy: kod wersji -> liczba stojących -> wpis */
export type VersionMassDataTable = Record<
  string,
  Partial<Record<StandingPassengersKey, MassDataEntry>>
>;

/** Tabele danych masowych zarejestrowane per zabudowa */
const MASS_DATA_TABLES: Partial<Record<VehicleLine, VersionMassDataTable>> = {
  [VehicleLine.IvecoTouristLine]: IVECO_MASS_DATA,
  [VehicleLine.MercedesTouristLineHighDeck]:
    MERCEDES_TOURIST_LINE_HIGHDECK_MASS_DATA,
  [VehicleLine.MercedesTouristLine]: MERCEDES_TOURIST_LINE_MASS_DATA,
};

/**
 * Niektóre zabudowy kodują w tabeli źródłowej "dziesiątkę" tonażu wieloznacznie
 * (np. Iveco: "8X" zamiast "80".."89" - patrz iveco-mass-data.ts). Dla takich
 * zabudów rejestrujemy tu funkcję generującą dodatkowe kandydujące klucze do
 * przeszukania w tabeli, poza dokładnym dopasowaniem wersji.
 */
const VERSION_CANDIDATE_EXPANDERS: Partial<
  Record<VehicleLine, (version: string) => string[]>
> = {
  [VehicleLine.IvecoTouristLine]: expandIvecoVersionCandidates,
};

/** Czy dla podanej zabudowy mamy zarejestrowaną (niepustą) tabelę danych masowych */
export function hasMassDataTable(line: VehicleLine): boolean {
  const table = MASS_DATA_TABLES[line];
  return !!table && Object.keys(table).length > 0;
}

/**
 * Wyznacza dane masowe dla podanej zabudowy, kodu wersji pojazdu i liczby
 * stojących pasażerów. Jeśli nie znaleziono dopasowania dla dokładnej liczby
 * stojących, korzysta z wpisu "*" (gdy liczba stojących nie wpływa na wynik).
 *
 * @param line - Zabudowa (VehicleLine)
 * @param version - Kod wersji pojazdu (np. "72W02R0L0", "62W05NR1L0M0")
 * @param standingPlacesCount - Liczba miejsc stojących z zamówienia
 * @returns Dane masowe lub null, jeśli nie znaleziono dopasowania
 */
export function getMassData(
  line: VehicleLine,
  version: string | undefined | null,
  standingPlacesCount: number | string | undefined | null,
): MassDataEntry | null {
  if (!version) return null;

  const table = MASS_DATA_TABLES[line];
  if (!table) return null;

  const expandCandidates = VERSION_CANDIDATE_EXPANDERS[line];
  const candidates = expandCandidates ? expandCandidates(version) : [version];

  const standingKey = String(standingPlacesCount ?? "");

  for (const candidate of candidates) {
    const entriesForVersion = table[candidate];
    if (!entriesForVersion) continue;
    const entry =
      entriesForVersion[standingKey as StandingPassengersKey] ??
      entriesForVersion["*"];
    if (entry) return entry;
  }

  return null;
}
